import { z } from "zod";

import { canonicalSha256 } from "./canonicalJson";

export const IDENTITY_SCENARIO_FIXTURE_SHA256 =
  "7ad6a5d3a93dee4401bde4efb8ac61259a50f0167ba5220c10416ce575d6d85e";

const digestSchema = z.string().regex(/^sha256:[a-f0-9]{64}$/);
const compactManifestVersionSchema = z.preprocess(
  (value) => (value == null || value === 0 ? null : value),
  z.number().int().positive().nullable(),
);
const compactManifestDigestSchema = z.preprocess(
  (value) => (value == null ? null : value),
  digestSchema.nullable(),
);
const environmentSchema = z.enum(["staging", "stg"]);
const manifestState = new Set([
  "not_applicable",
  "pending",
  "draft",
  "awaiting_confirmation",
  "frozen",
  "superseded",
  "partial",
  "failed_safe",
  "deleted",
  "unknown",
]);
const availability = new Set([
  "available",
  "partial",
  "unavailable",
  "deleted",
  "legacy",
  "unknown",
]);
const species = new Set(["dog", "cat", "other", "unknown"]);
const confidence = new Set(["high", "medium", "low", "rejected", "unknown"]);
const resolution = new Set([
  "resolved",
  "provisional",
  "abstained",
  "user_confirmed",
  "operator_corrected",
  "unknown",
]);

const modelReceiptSchema = z
  .object({
    registry_id: z.string().min(1),
    artifact_digest: digestSchema,
    role: z.enum(["detector", "tracker", "pet_embedder", "calibration"]),
  })
  .passthrough();

const subjectSchema = z
  .object({
    echo_identity_id: z.string().min(1),
    kind: z.string(),
    species: z.string(),
    confidence: z.string(),
    resolution_status: z.string(),
    distinct_media_count: z.number().int().nonnegative(),
    distinct_tracklet_count: z.number().int().nonnegative(),
    references: z.array(z.record(z.string(), z.unknown())),
  })
  .passthrough();

const instanceSchema = z
  .object({
    instance_id: z.string().min(1),
    source_media_id: z.string().min(1),
    source_content_digest: digestSchema,
  })
  .passthrough();

const artifactSchema = z
  .object({
    artifact_id: z.string().min(1),
    echo_id: z.string().min(1),
    environment: environmentSchema,
    content_digest: digestSchema,
  })
  .passthrough();

const manifestSchema = z
  .object({
    schema_version: z.literal("identity-manifest.v1"),
    environment: environmentSchema,
    product_flow: z.literal("b2c"),
    echo_id: z.string().min(1),
    identity_manifest_id: z.string().min(1),
    manifest_version: z.number().int().positive(),
    status: z.string(),
    media_set_digest: digestSchema,
    manifest_digest: digestSchema,
    config_release_id: z.string().min(1),
    config_digest: digestSchema,
    model_release: z.array(modelReceiptSchema).min(1),
    subjects: z.array(subjectSchema),
    instances: z.array(instanceSchema),
    tracklets: z.array(z.record(z.string(), z.unknown())),
    assignments: z.array(z.record(z.string(), z.unknown())),
    unknown_instance_ids: z.array(z.string()),
    decisions: z.array(z.record(z.string(), z.unknown())),
    quality_summary: z
      .object({
        availability: z.string(),
        failed_asset_count: z.number().int().nonnegative(),
        reasons: z.array(z.string()),
      })
      .passthrough(),
    artifact_refs: z.array(artifactSchema),
  })
  .passthrough();

const subjectSummarySchema = z.object({
  echo_identity_id: z.string().min(1),
  species: z.string(),
  confidence: z.string(),
  resolution_status: z.string(),
  distinct_media_count: z.number().int().nonnegative(),
  distinct_tracklet_count: z.number().int().nonnegative(),
  reference_count: z.number().int().nonnegative(),
});

const summarySchema = z
  .object({
    schema_version: z.literal("identity-summary.v1"),
    availability: z.string(),
    manifest_status: z.string(),
    // Failed-safe and legacy producer projections use 0/omission to mean that
    // no manifest was frozen. Normalize those wire sentinels to the compact
    // consumer contract's single representation instead of rejecting the
    // entire privacy-safe summary.
    manifest_version: compactManifestVersionSchema,
    manifest_digest: compactManifestDigestSchema,
    subject_count: z.number().int().nonnegative(),
    unknown_instance_count: z.number().int().nonnegative(),
    failed_asset_count: z.number().int().nonnegative(),
    confirmation_required: z.boolean(),
    mode: z
      .enum(["disabled", "observe", "ask", "enforce", "unknown"])
      .optional()
      .default("unknown"),
    inference_executed: z.boolean().nullable().optional().default(null),
    reasons: z.array(z.string()),
    subjects: z.array(subjectSummarySchema),
  })
  .passthrough();

const inspectorSchema = z
  .object({
    schema_version: z.literal("identity-inspector.v1"),
    summary: summarySchema,
    decision_ids: z.array(z.string()),
    artifact_ids: z.array(z.string()),
    cannot_link_count: z.number().int().nonnegative(),
    constraint_conflict_count: z.number().int().nonnegative(),
    downstream_result: z.string(),
  })
  .passthrough();

export type IdentityManifestV1 = z.infer<typeof manifestSchema>;
export type IdentitySummaryV1 = z.infer<typeof summarySchema>;
export type IdentityInspectorV1 = z.infer<typeof inspectorSchema>;

export class IdentityContractError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = "IdentityContractError";
  }
}

function known(value: string, values: Set<string>) {
  return values.has(value) ? value : "unknown";
}

function unique(kind: string, values: string[]) {
  const seen = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) {
      throw new IdentityContractError(`duplicate_identity_${kind}_id:${value}`);
    }
    seen.add(value);
  }
}

function scanManifest(value: unknown, path = "$") {
  if (Array.isArray(value)) {
    value.forEach((item, index) => scanManifest(item, `${path}[${index}]`));
    return;
  }
  if (value === null || typeof value !== "object") return;
  for (const [key, item] of Object.entries(value)) {
    const lowered = key.toLowerCase();
    if (
      ["embedding", "vector", "signed_url"].some((marker) =>
        lowered.includes(marker),
      )
    ) {
      throw new IdentityContractError(
        `identity_manifest_forbidden_field:${path}.${key}`,
      );
    }
    scanManifest(item, `${path}.${key}`);
  }
}

function scanCompact(value: unknown, path = "$") {
  if (Array.isArray(value)) {
    if (value.length > 8 && value.every((item) => typeof item === "number")) {
      throw new IdentityContractError(`identity_compact_vector_shaped:${path}`);
    }
    value.forEach((item, index) => scanCompact(item, `${path}[${index}]`));
    return;
  }
  if (typeof value === "string") {
    const lowered = value.toLowerCase();
    if (
      ["http://", "https://", "s3://", "gs://", "file://"].some((prefix) =>
        lowered.startsWith(prefix),
      )
    ) {
      throw new IdentityContractError(
        `identity_compact_forbidden_locator:${path}`,
      );
    }
    return;
  }
  if (value === null || typeof value !== "object") return;
  for (const [key, item] of Object.entries(value)) {
    const lowered = key.toLowerCase();
    const markers = [
      "embedding",
      "vector",
      "storage_key",
      "bucket",
      "url",
      "uri",
      "path",
      "display_label",
      "name",
    ];
    if (markers.some((marker) => lowered.includes(marker))) {
      throw new IdentityContractError(
        `identity_compact_forbidden_key:${path}.${key}`,
      );
    }
    scanCompact(item, `${path}.${key}`);
  }
}

export function parseIdentitySummary(value: unknown): IdentitySummaryV1 {
  scanCompact(value);
  const parsed = summarySchema.parse(value);
  return {
    ...parsed,
    availability: known(parsed.availability, availability),
    manifest_status: known(parsed.manifest_status, manifestState),
    subjects: parsed.subjects.map((subject) => ({
      ...subject,
      species: known(subject.species, species),
      confidence: known(subject.confidence, confidence),
      resolution_status: known(subject.resolution_status, resolution),
    })),
  };
}

export function parseIdentityInspector(value: unknown): IdentityInspectorV1 {
  scanCompact(value);
  const parsed = inspectorSchema.parse(value);
  return { ...parsed, summary: parseIdentitySummary(parsed.summary) };
}

export async function parseIdentityManifest(
  value: unknown,
  expected: { environment: "staging" | "stg"; echoId: string },
): Promise<{ manifest: IdentityManifestV1; summary: IdentitySummaryV1 }> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new IdentityContractError("identity_manifest_not_an_object");
  }
  const raw = value as Record<string, unknown>;
  if (raw.schema_version !== "identity-manifest.v1") {
    throw new IdentityContractError("identity_manifest_major_unsupported");
  }
  const { manifest_digest: suppliedDigest, ...payload } = raw;
  const expectedDigest = `sha256:${await canonicalSha256(payload)}`;
  if (suppliedDigest !== expectedDigest) {
    throw new IdentityContractError("identity_manifest_digest_mismatch");
  }
  scanManifest(raw);
  const manifest = manifestSchema.parse(raw);
  if (manifest.environment !== expected.environment) {
    throw new IdentityContractError("identity_manifest_environment_mismatch");
  }
  if (manifest.echo_id !== expected.echoId) {
    throw new IdentityContractError("identity_manifest_echo_mismatch");
  }
  if (manifest.subjects.some((subject) => subject.kind === "person")) {
    throw new IdentityContractError("human_identity_hard_denied");
  }
  unique(
    "subject",
    manifest.subjects.map((subject) => subject.echo_identity_id),
  );
  unique(
    "instance",
    manifest.instances.map((instance) => instance.instance_id),
  );
  for (const artifact of manifest.artifact_refs) {
    if (
      artifact.environment !== expected.environment ||
      artifact.echo_id !== expected.echoId
    ) {
      throw new IdentityContractError(
        "identity_artifact_cross_scope_reference",
      );
    }
  }

  const summary = parseIdentitySummary({
    schema_version: "identity-summary.v1",
    availability: known(manifest.quality_summary.availability, availability),
    manifest_status: known(manifest.status, manifestState),
    manifest_version: manifest.manifest_version,
    manifest_digest: manifest.manifest_digest,
    subject_count: manifest.subjects.length,
    unknown_instance_count: manifest.unknown_instance_ids.length,
    failed_asset_count: manifest.quality_summary.failed_asset_count,
    confirmation_required: manifest.status === "awaiting_confirmation",
    reasons: manifest.quality_summary.reasons,
    subjects: manifest.subjects.map((subject) => ({
      echo_identity_id: subject.echo_identity_id,
      species: known(subject.species, species),
      confidence: known(subject.confidence, confidence),
      resolution_status: known(subject.resolution_status, resolution),
      distinct_media_count: subject.distinct_media_count,
      distinct_tracklet_count: subject.distinct_tracklet_count,
      reference_count: subject.references.length,
    })),
  });
  return { manifest, summary };
}
